import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { writeAdminAuditEvent } from 'lib/adminAudit';
import { getExternalDb } from 'lib/firebase';
import { markStaticSeoRefreshRequired } from 'lib/seoRefreshStatus';
import { requestPublicBlog } from 'lib/siteBlogApi';

const BLOG_COLLECTION = 'blog_posts';
const DEFAULT_BLOG_VISUAL_THEME = 'editorial-parallax';
const DEFAULT_BLOG_ACCENT_COLOR = '#d4af37';

export const BLOG_VISUAL_THEMES = [
  {
    value: 'editorial-parallax',
    label: 'Editorial Parallax',
    accentColor: '#d4af37',
    description: 'Cinematic image-led case page with parallax hero, metrics, pillars, and chapters.',
  },
  {
    value: 'wine-split',
    label: 'Wine Split',
    accentColor: '#e9c46a',
    description: 'Split image and color-block page inspired by the app-main consumer case.',
  },
  {
    value: 'data-room',
    label: 'Data Room',
    accentColor: '#34d399',
    description: 'Terminal/dashboard page with KPI tiles and data-room styling.',
  },
  {
    value: 'process-index',
    label: 'Process Index',
    accentColor: '#7dd3fc',
    description: 'Process page with sticky index, numbered timeline, and cool accent.',
  },
];

const BLOG_VISUAL_THEME_VALUES = new Set(BLOG_VISUAL_THEMES.map((theme) => theme.value));

function getDbOrThrow() {
  const db = getExternalDb();
  if (!db) {
    throw new Error('Blog CMS is not configured. Check Firebase environment variables.');
  }
  return db;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sanitizeText(value) {
  return String(value || '').trim();
}

export function normalizeBlogVisualTheme(value) {
  const theme = sanitizeText(value).toLowerCase();
  return BLOG_VISUAL_THEME_VALUES.has(theme) ? theme : DEFAULT_BLOG_VISUAL_THEME;
}

export function normalizeBlogAccentColor(value, visualTheme = DEFAULT_BLOG_VISUAL_THEME) {
  const color = sanitizeText(value);
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return color.toLowerCase();
  }
  const theme = BLOG_VISUAL_THEMES.find((item) => item.value === normalizeBlogVisualTheme(visualTheme));
  return theme?.accentColor || DEFAULT_BLOG_ACCENT_COLOR;
}

export function slugifyBlogTitle(value) {
  return sanitizeText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
}

function splitTags(value) {
  if (Array.isArray(value)) {
    return value.map(sanitizeText).filter(Boolean).slice(0, 16);
  }

  return sanitizeText(value)
    .split(',')
    .map(sanitizeText)
    .filter(Boolean)
    .slice(0, 16);
}

function normalizeLabelValueItems(value, { maxItems = 8, maxLabelLength = 120, maxValueLength = 240 } = {}) {
  const source = Array.isArray(value)
    ? value
    : sanitizeText(value)
      .split(/\n+/)
      .map((line) => {
        const [label, ...rest] = line.split(/\s+\|\s+|::|:/);
        return { label, value: rest.join(' ').trim() };
      });

  return source
    .map((item) => {
      if (Array.isArray(item)) {
        return {
          label: sanitizeText(item[0]).slice(0, maxLabelLength),
          value: sanitizeText(item[1]).slice(0, maxValueLength),
        };
      }
      if (!item || typeof item !== 'object') return null;
      return {
        label: sanitizeText(item.label || item.title || item.name || item.heading).slice(0, maxLabelLength),
        value: sanitizeText(item.value || item.detail || item.body || item.text).slice(0, maxValueLength),
      };
    })
    .filter((item) => item?.label && item?.value)
    .slice(0, maxItems);
}

function normalizeChapterItems(value) {
  return normalizeLabelValueItems(value, {
    maxItems: 12,
    maxLabelLength: 160,
    maxValueLength: 2200,
  }).map((item) => ({
    heading: item.label,
    body: item.value,
  }));
}

function normalizeBlogContentRows(value) {
  const source = Array.isArray(value)
    ? value
    : sanitizeText(value)
      .split(/\n+/)
      .map((line) => {
        const parts = line.split(/\s+\|\s+/);
        return {
          eyebrow: parts[0],
          heading: parts[1],
          leftText: parts[2],
          body: parts.slice(3).join(' | '),
        };
      });

  return source
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const heading = sanitizeText(row.heading || row.title || row.label).slice(0, 180);
      const body = sanitizeText(row.body || row.value || row.detail || row.text).slice(0, 2600);
      const eyebrow = sanitizeText(row.eyebrow || row.kicker || row.number).slice(0, 80);
      const leftText = sanitizeText(row.leftText || row.leftBody || row.sidebar || row.summary).slice(0, 700);
      if (!heading && !body && !leftText) return null;
      return { eyebrow, heading, leftText, body };
    })
    .filter(Boolean)
    .slice(0, 12);
}

function normalizePillarItems(value) {
  return normalizeLabelValueItems(value, {
    maxItems: 6,
    maxLabelLength: 120,
    maxValueLength: 800,
  }).map((item) => ({
    title: item.label,
    detail: item.value,
  }));
}

function normalizeTextItems(value, maxItems = 10, maxLength = 240) {
  const source = Array.isArray(value)
    ? value
    : sanitizeText(value).split(/\n+/);
  return source
    .map(sanitizeText)
    .filter(Boolean)
    .map((item) => item.slice(0, maxLength))
    .slice(0, maxItems);
}

function normalizePostSnapshot(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    title: sanitizeText(data.title),
    slug: sanitizeText(data.slug),
    excerpt: sanitizeText(data.excerpt),
    body: sanitizeText(data.body),
    status: sanitizeText(data.status) === 'published' ? 'published' : 'draft',
    authorName: sanitizeText(data.authorName),
    featuredImageUrl: sanitizeText(data.featuredImageUrl),
    featuredImageAlt: sanitizeText(data.featuredImageAlt),
    category: sanitizeText(data.category),
    tags: splitTags(data.tags),
    visualTheme: normalizeBlogVisualTheme(data.visualTheme || data.caseTemplate),
    accentColor: normalizeBlogAccentColor(data.accentColor, data.visualTheme || data.caseTemplate),
    heroStatValue: sanitizeText(data.heroStatValue),
    heroStatLabel: sanitizeText(data.heroStatLabel),
    metaBlocks: normalizeLabelValueItems(data.metaBlocks, { maxItems: 4 }),
    metrics: normalizeLabelValueItems(data.metrics, { maxItems: 4 }),
    chapters: normalizeChapterItems(data.chapters),
    contentRows: normalizeBlogContentRows(data.contentRows),
    pillars: normalizePillarItems(data.pillars),
    readingSectionHeading: sanitizeText(data.readingSectionHeading),
    deliverables: normalizeTextItems(data.deliverables, 12, 280),
    pullQuote: sanitizeText(data.pullQuote),
    pullQuoteSource: sanitizeText(data.pullQuoteSource),
    seoTitle: sanitizeText(data.seoTitle),
    metaDescription: sanitizeText(data.metaDescription),
    canonicalPath: sanitizeText(data.canonicalPath),
    defaultSlug: sanitizeText(data.defaultSlug),
    publishedAt: data.publishedAt || null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdBy: sanitizeText(data.createdBy),
    updatedBy: sanitizeText(data.updatedBy),
  };
}

function sortPostsNewestFirst(posts) {
  return [...posts].sort((a, b) => {
    const bTime = timestampToMillis(b.publishedAt) || timestampToMillis(b.updatedAt) || timestampToMillis(b.createdAt);
    const aTime = timestampToMillis(a.publishedAt) || timestampToMillis(a.updatedAt) || timestampToMillis(a.createdAt);
    return bTime - aTime;
  });
}

export function formatBlogDate(value) {
  const millis = timestampToMillis(value);
  if (!millis) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(millis));
}

export function buildEmptyBlogPost() {
  return {
    title: '',
    slug: '',
    excerpt: '',
    body: '',
    status: 'draft',
    authorName: 'Magellan',
    featuredImageUrl: '',
    featuredImageAlt: '',
    category: '',
    tags: '',
    visualTheme: DEFAULT_BLOG_VISUAL_THEME,
    accentColor: DEFAULT_BLOG_ACCENT_COLOR,
    heroStatValue: '',
    heroStatLabel: '',
    metaBlocks: '',
    metrics: '',
    chapters: '',
    contentRows: [],
    pillars: '',
    readingSectionHeading: '',
    deliverables: '',
    pullQuote: '',
    pullQuoteSource: '',
    seoTitle: '',
    metaDescription: '',
  };
}

export async function listPublishedBlogPosts() {
  const posts = new Map();
  const cursors = new Set();
  let after = null;
  do {
    const page = await requestPublicBlog(`?limit=100${after ? `&after=${after}` : ''}`);
    if (!page || !Array.isArray(page.items) || (page.next !== null && !/^[A-Za-z0-9_-]{1,128}$/.test(page.next || ''))) {
      throw new Error('Articles could not load from the website. Please try again later.');
    }
    page.items.forEach((data) => {
      if (data?.status !== 'published' || !data.id || !data.slug || !data.title) return;
      const post = normalizePostSnapshot({ id: data.id, data: () => data });
      posts.set(post.id, post);
    });
    after = page.next;
    if (after && cursors.has(after)) throw new Error('Articles could not load from the website. Please try again later.');
    if (after) cursors.add(after);
  } while (after);
  return sortPostsNewestFirst([...posts.values()]);
}

export async function getPublishedBlogPostBySlug(slug) {
  const normalizedSlug = sanitizeText(slug).toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSlug) || normalizedSlug.length > 150) return null;
  try {
    const data = await requestPublicBlog(`/by-slug/${normalizedSlug}`);
    if (!data || data.status !== 'published' || data.slug !== normalizedSlug || !data.id || !data.title) {
      throw new Error('This article could not load from the website. Please try again later.');
    }
    return normalizePostSnapshot({ id: data.id, data: () => data });
  } catch (error) {
    if (error.status === 404 && error.code === 'not_found') return null;
    throw error;
  }
}

export async function listAdminBlogPosts() {
  const db = getDbOrThrow();
  const snapshot = await getDocs(collection(db, BLOG_COLLECTION));
  return sortPostsNewestFirst(snapshot.docs.map(normalizePostSnapshot));
}

export async function getAdminBlogPost(postId) {
  const db = getDbOrThrow();
  const snapshot = await getDoc(doc(db, BLOG_COLLECTION, postId));
  return snapshot.exists() ? normalizePostSnapshot(snapshot) : null;
}

function validateBlogPostInput(post) {
  const title = sanitizeText(post.title);
  const slug = slugifyBlogTitle(post.slug || title);
  const excerpt = sanitizeText(post.excerpt);
  const body = sanitizeText(post.body);
  const status = sanitizeText(post.status) === 'published' ? 'published' : 'draft';
  const metaDescription = sanitizeText(post.metaDescription);
  const visualTheme = normalizeBlogVisualTheme(post.visualTheme || post.caseTemplate);
  const accentColor = normalizeBlogAccentColor(post.accentColor, visualTheme);
  const metaBlocks = normalizeLabelValueItems(post.metaBlocks, { maxItems: 4 });
  const metrics = normalizeLabelValueItems(post.metrics, { maxItems: 4 });
  const chapters = normalizeChapterItems(post.chapters);
  const contentRows = normalizeBlogContentRows(post.contentRows);
  const pillars = normalizePillarItems(post.pillars);
  const deliverables = normalizeTextItems(post.deliverables, 12, 280);
  const hasStructuredBody = contentRows.some((row) => row.body);

  if (!title) throw new Error('Title is required.');
  if (!slug) throw new Error('Slug is required.');

  if (status === 'published') {
    if (!excerpt) throw new Error('Published posts require an excerpt.');
    if (!body && !hasStructuredBody) throw new Error('Published posts require body content or article row body text.');
    if (!metaDescription) throw new Error('Published posts require a meta description.');
    if (!sanitizeText(post.category)) throw new Error('Published posts require a category.');
    if (!sanitizeText(post.featuredImageUrl)) throw new Error('Published posts require a featured image.');
    if (!sanitizeText(post.featuredImageAlt)) throw new Error('Published posts require featured image alt text.');
  }

  return {
    title,
    slug,
    excerpt,
    body,
    status,
    authorName: sanitizeText(post.authorName) || 'Magellan',
    featuredImageUrl: sanitizeText(post.featuredImageUrl),
    featuredImageAlt: sanitizeText(post.featuredImageAlt),
    category: sanitizeText(post.category),
    tags: splitTags(post.tags),
    visualTheme,
    accentColor,
    heroStatValue: sanitizeText(post.heroStatValue).slice(0, 32),
    heroStatLabel: sanitizeText(post.heroStatLabel).slice(0, 120),
    metaBlocks,
    metrics,
    chapters,
    contentRows,
    pillars,
    readingSectionHeading: sanitizeText(post.readingSectionHeading).slice(0, 180),
    deliverables,
    pullQuote: sanitizeText(post.pullQuote).slice(0, 800),
    pullQuoteSource: sanitizeText(post.pullQuoteSource).slice(0, 160),
    seoTitle: sanitizeText(post.seoTitle) || title,
    metaDescription,
    canonicalPath: `/blog/${slug}`,
  };
}

async function assertUniqueSlug(slug, currentPostId = '') {
  const posts = await listAdminBlogPosts();
  const conflict = posts.find((post) => post.slug === slug && post.id !== currentPostId);
  if (conflict) {
    throw new Error(`The slug "${slug}" is already used by another post.`);
  }
}

export async function saveBlogPost({ postId = '', post, adminEmail = '' }) {
  const db = getDbOrThrow();
  const payload = validateBlogPostInput(post);
  await assertUniqueSlug(payload.slug, postId);
  const targetPostId = sanitizeText(postId);

  const auditFields = {
    ...payload,
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeText(adminEmail),
  };

  if (payload.status === 'published') {
    auditFields.publishedAt = post.publishedAt || serverTimestamp();
  }

  if (targetPostId) {
    await updateDoc(doc(db, BLOG_COLLECTION, targetPostId), auditFields);
    await writeAdminAuditEvent({
      action: payload.status === 'published' ? 'updated-published' : 'updated-draft',
      targetCollection: BLOG_COLLECTION,
      targetId: targetPostId,
      summary: 'Blog post saved',
      adminEmail,
      metadata: {
        slug: payload.slug,
        status: payload.status,
        title: payload.title,
        category: payload.category,
      },
    });
    if (payload.status === 'published') {
      await markStaticSeoRefreshRequired({
        action: 'updated',
        source: 'blog',
        path: `/blog/${payload.slug}`,
        title: payload.title,
        adminEmail,
      }).catch((error) => {
        console.error('Failed to mark static SEO refresh required after blog update:', error);
      });
    }
    return targetPostId;
  }

  const created = await addDoc(collection(db, BLOG_COLLECTION), {
    ...auditFields,
    createdAt: serverTimestamp(),
    createdBy: sanitizeText(adminEmail),
  });
  await writeAdminAuditEvent({
    action: payload.status === 'published' ? 'created-published' : 'created-draft',
    targetCollection: BLOG_COLLECTION,
    targetId: created.id,
    summary: 'Blog post created',
    adminEmail,
    metadata: {
      slug: payload.slug,
      status: payload.status,
      title: payload.title,
      category: payload.category,
    },
  });
  if (payload.status === 'published') {
    await markStaticSeoRefreshRequired({
      action: 'published',
      source: 'blog',
      path: `/blog/${payload.slug}`,
      title: payload.title,
      adminEmail,
    }).catch((error) => {
      console.error('Failed to mark static SEO refresh required after blog publish:', error);
    });
  }
  return created.id;
}

export async function deleteBlogPost(postId) {
  const db = getDbOrThrow();
  const id = sanitizeText(postId);
  if (!id) {
    throw new Error('Post ID is required.');
  }

  await deleteDoc(doc(db, BLOG_COLLECTION, id));
  await writeAdminAuditEvent({
    action: 'deleted',
    targetCollection: BLOG_COLLECTION,
    targetId: id,
    summary: 'Blog post deleted',
  });
  await markStaticSeoRefreshRequired({
    action: 'deleted',
    source: 'blog',
    path: '/blog',
    title: 'Blog post deleted',
  }).catch((error) => {
    console.error('Failed to mark static SEO refresh required after blog delete:', error);
  });
}
